import axios from 'axios';
import API from '../apiClient';
import { createSSEStream } from '../Utils/sseStream';
import { applyZavvyDisabled, isZavvyDisabledError } from '../Components/FocusMode/Zavvy/orgFeatures';

function baseUrlFromAzureChatEnv() {
  const chat = process.env.REACT_APP_AZURE_AI_BACKEND_URL;
  if (!chat) return undefined;
  return chat
    .replace(/\/api\/v1\/chat\/?$/, '')
    .replace(/\/api\/chat\/?$/, '');
}

export const AI_BACKEND_URL =
  process.env.REACT_APP_AI_BACKEND_URL ||
  baseUrlFromAzureChatEnv() ||
  'http://localhost:5000';

const aiClient = () => {
  const token = localStorage.getItem('jwtToken');
  const client = axios.create({
    baseURL: AI_BACKEND_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    timeout: 60000,
  });
  client.interceptors.response.use(
    (res) => res,
    (err) => {
      if (isZavvyDisabledError(err)) applyZavvyDisabled();
      return Promise.reject(err);
    },
  );
  return client;
};

const ai = (method, url, data, config) => aiClient()[method](url, data, config);

export const getZavvyMe = () => ai('get', '/api/v1/zavvy/me');
export const listSessions = (params) => ai('get', '/api/v1/zavvy/sessions', { params });
export const createSession = (body) => ai('post', '/api/v1/zavvy/sessions', body);
export const getSession = (id) => ai('get', `/api/v1/zavvy/sessions/${id}`);
export const patchSession = (id, body) => ai('patch', `/api/v1/zavvy/sessions/${id}`, body);
export const deleteSession = (id) => ai('post', `/api/v1/zavvy/sessions/${id}/delete`);
export const restoreSession = (id) => ai('post', `/api/v1/zavvy/sessions/${id}/restore`);
export const cancelTurn = (id) => ai('post', `/api/v1/zavvy/sessions/${id}/cancel`);
export const sendFeedback = (sessionId, messageId, body) =>
  ai('post', `/api/v1/zavvy/sessions/${sessionId}/messages/${messageId}/feedback`, body);
export const patchCardState = (messageId, cardKey, body) =>
  ai('patch', `/api/v1/zavvy/cards/${messageId}/${cardKey}`, body);
export const getShareTargets = () => ai('get', '/api/v1/zavvy/share-targets');
export const getShare = (id) => ai('get', `/api/v1/zavvy/sessions/${id}/share`);
export const putShare = (id, body) => ai('put', `/api/v1/zavvy/sessions/${id}/share`, body);
export const getPrefs = () => ai('get', '/api/v1/zavvy/prefs');
export const putPrefs = (body) => ai('put', '/api/v1/zavvy/prefs', body);
export const retentionPreview = (days) =>
  ai('get', '/api/v1/zavvy/prefs/retention-preview', { params: { days } });
export const getReminders = (deliver = false) =>
  ai('get', '/api/v1/zavvy/reminders', { params: { deliver } });
export const putReminder = (subjectType, subjectId, body) =>
  ai('put', `/api/v1/zavvy/reminders/${subjectType}/${encodeURIComponent(subjectId)}`, body);
export const listGroups = () => ai('get', '/api/v1/zavvy/groups');
export const createGroup = (body) => ai('post', '/api/v1/zavvy/groups', body);
export const patchGroup = (id, body) => ai('patch', `/api/v1/zavvy/groups/${id}`, body);
export const deleteGroup = (id) => ai('post', `/api/v1/zavvy/groups/${id}/delete`);

/** Multipart upload into a session. fields: { purpose, save_as_evidence?, note_id? } */
export const uploadAttachment = (sessionId, file, fields = {}, config = {}) => {
  const form = new FormData();
  form.append('file', file);
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null) form.append(k, String(v)); });
  return ai('post', `/api/v1/zavvy/sessions/${sessionId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    ...config,
  });
};
/** Attaches a file the browser already saved in the Document Center (source 'Zavvy Chat Upload'). body: { policy_center_id, file_id, purpose? } */
export const attachFromDocumentCenter = (sessionId, body) =>
  ai('post', `/api/v1/zavvy/sessions/${sessionId}/attachments/from-document-center`, body, { timeout: 60000 });
// A status poll must not hang for the client default timeout, so each one is bounded (axios `get` takes config as its 2nd argument).
export const getAttachment = (sessionId, attachmentId) =>
  ai('get', `/api/v1/zavvy/sessions/${sessionId}/attachments/${attachmentId}`, { timeout: 10000 });
export const deleteAttachment = (sessionId, attachmentId) =>
  ai('post', `/api/v1/zavvy/sessions/${sessionId}/attachments/${attachmentId}/delete`);

export const exportSessionUrl = (id, format) =>
  `${AI_BACKEND_URL}/api/v1/zavvy/sessions/${id}/export?format=${format}`;

/** The file name from a Content-Disposition header; the RFC 5987 `filename*` form wins. */
export const filenameFrom = (disposition, fallback) => {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition || '');
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through to the plain form */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition || '');
  return plain ? plain[1].trim() : fallback;
};

/**
 * A file Zavvy built (card kind "file"), re-rendered in `format` (xlsx | docx | pptx; the built
 * format when omitted). Resolves { blob, fileName }. Goes through axios so the JWT is sent;
 * never open the URL in a new tab.
 */
export const downloadDocument = async (documentId, format) => {
  const res = await aiClient().get(`/api/v1/zavvy/documents/${documentId}/download`, {
    params: format ? { format } : {},
    responseType: 'blob',
    timeout: 120000,
  });
  return { blob: res.data, fileName: filenameFrom(res.headers?.['content-disposition'], `zavvy-document.${format || 'xlsx'}`) };
};
/** Uploads a throwaway copy for the Office viewer. Resolves { sas_url, file_name, expires_at }. */
export const previewDocument = (documentId, format) =>
  ai('post', `/api/v1/zavvy/documents/${documentId}/preview`, { format }, { timeout: 120000 });
/** Saves a copy into Document Center. body: { format, type, name?, description? } */
export const saveDocument = (documentId, body) =>
  ai('post', `/api/v1/zavvy/documents/${documentId}/save`, body, { timeout: 120000 });
export const getDocument = (documentId) => ai('get', `/api/v1/zavvy/documents/${documentId}`);

// Zavvy Policy Reviewer (docs/2026-10-28-ZAVVY_POLICY_REVIEW_CARD.md). The review runs in the background on
// the AI backend; the chat card and the side panel poll these reads until the status is terminal.
export const getPolicyReview = (reviewId) => ai('get', `/api/v1/policy-review/${encodeURIComponent(reviewId)}`, { timeout: 15000 });
export const listSessionPolicyReviews = (sessionId) => ai('get', '/api/v1/policy-review', { params: { session_id: sessionId }, timeout: 15000 });
export const getPolicyReviewAspects = (reviewId) => ai('get', `/api/v1/policy-review/${encodeURIComponent(reviewId)}/aspects`, { timeout: 30000 });
/** The legal analysis for one aspect, as Markdown text. */
export const getPolicyReviewLegalAnalysis = (reviewId, aspectId, docType) =>
  ai('get', `/api/v1/policy-review/${encodeURIComponent(reviewId)}/aspects/${encodeURIComponent(aspectId)}/legal-analysis`, {
    params: docType ? { doc_type: docType } : {},
    responseType: 'text',
    timeout: 30000,
  });
/**
 * The Excel bundle or the Markdown report. The route answers 302 to a signed URL, which axios follows;
 * the file comes back as a blob so the JWT is sent on the first hop. Resolves { blob, fileName }.
 */
export const downloadPolicyReview = async (reviewId, format = 'excel') => {
  const res = await aiClient().get(`/api/v1/policy-review/${encodeURIComponent(reviewId)}/${format === 'markdown' ? 'markdown' : 'excel'}`, {
    responseType: 'blob',
    timeout: 120000,
  });
  const fallback = `policy-review-${reviewId}.${format === 'markdown' ? 'md' : 'xlsx'}`;
  return { blob: res.data, fileName: filenameFrom(res.headers?.['content-disposition'], fallback) };
};
/** { jurisdictions: [{ country, label, complete, issues[] }], default } for one policy type. */
export const listPolicyReviewJurisdictions = (policyType) =>
  ai('get', '/api/v1/policy-review/jurisdictions', { params: policyType ? { policy_type: policyType } : {}, timeout: 15000 });
/** Attaches an existing Document Center policy as the document to review (purpose policy_source, no ingestion). */
export const attachPolicySource = (sessionId, { policy_center_id, file_id }) =>
  attachFromDocumentCenter(sessionId, { policy_center_id, file_id, purpose: 'policy_source' });
/** Starts a review from a Document Center row in a new Zavvy session. body: { policy_center_id, doc_type?, country? }. Resolves { session_id, review_id, status }. */
export const startPolicyReview = (body) => ai('post', '/api/v1/zavvy/policy-reviews/start', body, { timeout: 120000 });
/**
 * What the Policy Reviewer detects in a Document Center row, with no review started. Resolves { doc_type, label,
 * country, confidence, reviewable, reason, aspect_count, sentence, start_doc_type, start_country, active }.
 */
export const detectPolicyReview = (policyCenterId) =>
  ai('post', '/api/v1/zavvy/policy-reviews/detect', { policy_center_id: policyCenterId }, { timeout: 60000 });

/**
 * Streams one turn. body: { message, sources?, edit_of?, build? } where build is
 * { format: 'xlsx' | 'docx' | 'pptx' } when the user pressed Create in the Composer; the
 * body is passed through unchanged.
 */
export function streamMessage(sessionId, body, { onEvent, signal } = {}) {
  const controller = createSSEStream(
    `${AI_BACKEND_URL}/api/v1/zavvy/sessions/${sessionId}/messages`,
    body,
    {},
    () => {},
    () => {},
    (err) => onEvent?.('error', { message: err.message, code: err.code, status: err.status }),
    (type, data) => onEvent?.(type, data),
    { requireTerminal: true },
  );
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller;
}

export function regenerateMessage(sessionId, messageId, { onEvent, signal } = {}) {
  const controller = createSSEStream(
    `${AI_BACKEND_URL}/api/v1/zavvy/sessions/${sessionId}/messages/${messageId}/regenerate`,
    {},
    {},
    () => {},
    () => {},
    (err) => onEvent?.('error', { message: err.message, code: err.code, status: err.status }),
    (type, data) => onEvent?.(type, data),
    { requireTerminal: true },
  );
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller;
}

export const getRail = () => API.get('/focus-mode/rail');
/** One set of figures for every surface (iam_service docs/2026-10-21-FOCUS_MODE_SNAPSHOT_ENDPOINT.md); members get only `me`. */
export const getSnapshot = (projectId) => API.get('/focus-mode/snapshot', { params: projectId ? { project_id: projectId } : undefined });
export const getTimelineChecklist = (params) => API.get('/project/timeline-checklist', { params });
/** Ticks one card on the project's current stage, as the Timeline page's checkbox does. body: { card_key, done }. Returns the project's checklist. */
export const tickTimelineCard = (projectId, body) => API.put(`/project/${encodeURIComponent(projectId)}/timeline-checklist/tick`, body);

export const listRoutines = () => API.get('/zavvy-routine/');
export const routineTemplates = () => API.get('/zavvy-routine/templates');
export const createRoutine = (body) => API.post('/zavvy-routine/', body);
export const proposeRoutine = (body) => API.post('/zavvy-routine/propose', body);
export const confirmRoutine = (id) => API.post(`/zavvy-routine/${id}/confirm`);
export const updateRoutine = (id, body) => API.put(`/zavvy-routine/${id}`, body);
export const deleteRoutine = (id) => API.post(`/zavvy-routine/delete/${id}`);
export const runRoutine = (id) => API.post(`/zavvy-routine/${id}/run`);
export const pauseRoutine = (id) => API.post(`/zavvy-routine/${id}/pause`);
export const resumeRoutine = (id) => API.post(`/zavvy-routine/${id}/resume`);
export const routineRuns = (id) => API.get(`/zavvy-routine/${id}/runs`);
export const approveRoutineRun = (id, runId) => API.post(`/zavvy-routine/${id}/runs/${runId}/approve`);
export const skipRoutineRun = (id, runId) => API.post(`/zavvy-routine/${id}/runs/${runId}/skip`);

export const listNotifications = () => API.get('/user-notification/');
export const unreadCount = () => API.get('/user-notification/unread-count');
export const markNotificationRead = (id) => API.post(`/user-notification/${id}/read`);
export const markAllNotificationsRead = () => API.post('/user-notification/read-all');

export const getExternalAiAccess = (orgId) => API.get(`/organization/${orgId}/external-ai-access`);
export const putExternalAiAccess = (orgId, body) => API.put(`/organization/${orgId}/external-ai-access`, body);
export const getExternalAiClients = (orgId) => API.get(`/organization/${orgId}/external-ai-access/clients`);

const admin = (path, params) => ai('get', `/api/v1/admin/${path}`, { params });
export const zavvyUsage = {
  summary: (params) => admin('zavvy-usage/summary', params),
  byOrg: (params) => admin('zavvy-usage/by-org', params),
  byUser: (params) => admin('zavvy-usage/by-user', params),
  byAgent: (params) => admin('zavvy-usage/by-agent', params),
  routines: (params) => admin('zavvy-usage/routines', params),
  quality: (params) => admin('zavvy-usage/quality', params),
  retention: (params) => admin('zavvy-usage/retention', params),
  externalMcp: (params) => admin('zavvy-usage/external-mcp', params),
  csvUrl: () => `${AI_BACKEND_URL}/api/v1/admin/zavvy-usage/by-org.csv`,
  limits: () => ai('get', '/api/v1/admin/zavvy-limits'),
  putLimits: (body, orgId) => ai('put', orgId ? `/api/v1/admin/zavvy-limits/${orgId}` : '/api/v1/admin/zavvy-limits', body),
  prices: () => ai('get', '/api/v1/admin/ai-model-prices'),
  putPrice: (body) => ai('put', '/api/v1/admin/ai-model-prices', body),
};
